import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'
import { useEventSlug, useSlug, useQuery } from 'hooks'
import { NotFound } from 'components'
import FullPageLoading from 'components/FullPageLoading'
import {
    UpdatePresentationSpaceInput,
    GetPresentationQuery,
    GetPresentationQueryVariables,
} from 'generated/graphql'
import { updateShows } from 'features/shows/graphql/mutation'
import { _, withCapture } from 'utils'
import { getNodes } from 'graphql/utils'
import FormTabs from '../forms/FormTabs'
import { showNotification, showErrorNotification } from '../notifications/notificationsSlice'
import { updatePresentationSpace } from './graphql/mutation'
import { getPresentationQuery } from './graphql/query'
import Settings from './tabs/Settings'
import { getChangedValues } from '../forms/utils'
import { getEventData } from '../events/eventSlice'

const PresentationValidation = Yup.object().shape({
    parent: Yup.object().shape({
        name: Yup.string().required()
    }),
})

const PresentationSettings = () => {
    const eventSlug = useEventSlug()
    const presentationSlug = useSlug('presentationSlug')
    const dispatch = useDispatch()
    const [presentationId, setPresentationId] = useState<number>()
    const [isLoading, setIsLoading] = useState(false)
    const [initialValues, setInitialValues] = useState<UpdatePresentationSpaceInput>()
    const { data, requestStatus: status } = useQuery<
        GetPresentationQuery,
        GetPresentationQueryVariables
    >(getPresentationQuery, {
        eventId: eventSlug,
        filters: { parent: { slug: presentationSlug } }
    })

    // This effect watches the presentationSlug to fetch the presentation information and set it as the initial value
    // of the form.
    useEffect(() => {
        if (!data || status !== 'succeeded') {
            return
        }
        const presentationNode = getNodes(data.event.presentationSpaces)[0]
        if (!presentationNode) {
            setPresentationId(null)
            return
        }
        const {
            name, enableChat, helpContent, helpContentType, image, published
        } = presentationNode.parent
        const {
            showAttendees, streamIsContinues, streamUrl, interactionIsContinues, interactionUrl
        } = presentationNode
        setPresentationId(presentationNode.id)
        setInitialValues({
            showAttendees: showAttendees || false,
            streamIsContinues: streamIsContinues || false,
            streamUrl: streamUrl || '',
            interactionIsContinues: interactionIsContinues || false,
            interactionUrl: interactionUrl || '',
            parent: {
                name,
                image,
                enableChat,
                helpContent,
                helpContentType,
                published,
            }
        })
    }, [presentationSlug, status, data])

    if (status === 'failed' || presentationId === null) {
        return <NotFound />
    }

    if ((status !== 'succeeded') || !initialValues) {
        return <FullPageLoading />
    }

    const handleSubmit = withCapture(async (values: UpdatePresentationSpaceInput, { resetForm }) => {
        setIsLoading(true)
        const presentationSpace = getNodes(data.event.presentationSpaces)[0]
        const submitValues = getChangedValues(initialValues, values)
        if (submitValues.changeShowAttendees) {
            await updateShows(presentationSpace?.parent?.id, { showAttendees: values.showAttendees })
        }
        if (submitValues.changeEnableChat) {
            await updateShows(presentationSpace?.parent?.id, { enableChat: values.parent.enableChat })
        }
        delete submitValues.changeEnableChat
        delete submitValues.changeShowAttendees
        const response = await updatePresentationSpace(presentationId, submitValues)
        setIsLoading(false)

        if (!response.data || response.errors.length) {
            const messages = response.errors
            dispatch(showErrorNotification(messages))
            return
        }
        const newValues = {
            ...values,
            parent: {
                ...values.parent,
                image: response.data.updatePresentationSpace.parent.image
            }
        }
        resetForm({ values: newValues })
        setInitialValues(newValues)
        dispatch(showNotification({
            type: 'success',
            body: _('form.notification.update_success'),
        }))

        // If the name/published fields are changed, re-fetch event data for Sidebar
        if (submitValues?.parent?.name || submitValues?.parent?.published !== undefined) {
            dispatch(getEventData(eventSlug))
        }
    })

    return (
        <Formik
            key={presentationId}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={PresentationValidation}
        >
            <>
                <FormTabs
                    header={_('presentationsettings.header')}
                    tabs={[
                        {
                            id: 'settings',
                            name: _('presentationsettings.tabs.settings'),
                            Component: Settings
                        },
                    ]}
                    showHelpContent
                    showSpaceOpened
                />
                {isLoading && <FullPageLoading isAbsolute />}
            </>
        </Formik>
    )
}

export default PresentationSettings
